'use client';

import React from 'react';

import Link from 'next/link';

import { type BannerUrls } from '@/common/types/response.type';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';

interface SliderItem {
  link: string | null;
  bannerUrls: BannerUrls;
}

interface SliderProps {
  items: SliderItem[] | [];
}

interface BannerImageProps {
  bannerUrls: BannerUrls;
  isLargestContentfulPaintCandidate: boolean;
}

function BannerImage({
  bannerUrls,
  isLargestContentfulPaintCandidate,
}: BannerImageProps) {
  const desktopImage = bannerUrls.desktop ?? bannerUrls.mobile;

  return (
    <picture className="block h-full w-full">
      <source media="(min-width: 1024px)" srcSet={desktopImage} />
      <img
        src={bannerUrls.mobile}
        alt="بنر تبلیغاتی فروشگاه"
        width={1168}
        height={450}
        className="absolute inset-0 h-full w-full rounded-xl object-cover"
        decoding="async"
        loading={isLargestContentfulPaintCandidate ? 'eager' : 'lazy'}
        fetchPriority={
          isLargestContentfulPaintCandidate ? 'high' : 'auto'
        }
      />
    </picture>
  );
}

export default function BannerSlider({ items }: SliderProps) {
  if (!items || items.length === 0) return null;

  return (
    <div className="mt-0 w-full overflow-hidden rounded-xl lg:mb-10 lg:mt-6">
      <Swiper
        modules={[Pagination, Autoplay]}
        pagination={{
          clickable: true,
          el: '.custom-pagination',
          bulletActiveClass: 'bg-gray-700',
          bulletClass: 'w-4 h-2 mx-1 rounded-md bg-gray-400 cursor-pointer',
        }}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        loop={true}
        className="w-full overflow-hidden rounded-xl"
      >
        {items.map((item, index) => (
          <SwiperSlide key={index} className="overflow-hidden rounded-xl px-2">
            <div className="relative w-full aspect-[328/200] lg:aspect-[1168/450] overflow-hidden rounded-xl">
              {item.link ? (
                <Link
                  href={item.link}
                  className="block h-full w-full"
                  target="_blank"
                >
                  <BannerImage
                    bannerUrls={item.bannerUrls}
                    isLargestContentfulPaintCandidate={index === 0}
                  />
                </Link>
              ) : (
                <BannerImage
                  bannerUrls={item.bannerUrls}
                  isLargestContentfulPaintCandidate={index === 0}
                />
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* custom pagination bullets */}
      <div className="custom-pagination mt-4 flex justify-center" />
    </div>
  );
}
